import axios, {
  AxiosRequestConfig,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
  AxiosError,
} from "axios";
import { localStorageService } from "@/utils/localStorage";
import {
  ACCESS_TOKEN,
  REFRESH_TOKEN,
  REFRESH_TOKEN_URL,
  USER_INFO,
} from "./types";
import { normalizeJsonApiIfNeed } from "@/utils/normalize";

const getAuthToken = (): string | undefined => {
  return localStorageService.getItem(ACCESS_TOKEN) ?? undefined;
};

const getRefreshToken = (): string | undefined => {
  return localStorageService.getItem(REFRESH_TOKEN) ?? undefined;
};

const saveTokens = (accessToken: string, refreshToken?: string): void => {
  localStorageService.setItem(ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    localStorageService.setItem(REFRESH_TOKEN, refreshToken);
  }
  setAuthTokenHeader(accessToken);
};

const clearTokensAndLogout = (): void => {
  localStorageService.removeItem(ACCESS_TOKEN);
  localStorageService.removeItem(REFRESH_TOKEN);
  localStorageService.removeItem(USER_INFO);
  delete getApiClientInstance().defaults.headers.common["Authorization"];
  window.location.href = "/access-denied";
};

let isRefreshing = false;
let failedQueue: {
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (
  error: AxiosError | null,
  token: string | null = null
): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const requestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
  // Add Authorization header if token is available
  if (!config.headers.has("Authorization")) {
    const token = getAuthToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    } else {
      console.log("Request does not have auth key header.");
    }
  }
  return config;
};

const responseInterceptor = async (
  response: AxiosResponse
): Promise<AxiosResponse> => {
  const normalizedData = normalizeJsonApiIfNeed(response?.data);
  return Promise.resolve(normalizedData);
};

const errorInterceptor = async (error: AxiosError): Promise<any> => {
  const originalRequest = error?.config as InternalAxiosRequestConfig & {
    _retry?: boolean;
  };

  // Log general errors
  if (!error.response) {
    console.error(
      "Network Error or Timeout:",
      error.message,
      error.config?.url
    );
    return Promise.reject(error);
  }

  // console.error(
  //   "Error Interceptor:",
  //   error?.response?.status,
  //   error?.config?.url,
  //   error?.response?.data || error?.message
  // );

  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    if (originalRequest?.url === REFRESH_TOKEN_URL) {
      // console.error("Refresh token request failed with 401. Refresh token likely invalid.");
      clearTokensAndLogout();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest?.headers) {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
          }
          return getApiClientInstance()(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const currentRefreshToken = getRefreshToken();
    if (!currentRefreshToken) {
      // console.log("No refresh token available. Logging out.");
      isRefreshing = false;
      clearTokensAndLogout();
      return Promise.reject(error);
    }

    try {
      // console.log("Attempting token refresh...");
      // remove current authorization header
      // remove current token from local storage
      localStorageService.removeItem(ACCESS_TOKEN);
      delete getApiClientInstance().defaults.headers.common["Authorization"];
      const data = (await getApiClientInstance().post<{
        newAccessToken: string;
      }>(REFRESH_TOKEN_URL, {
        refresh_token: currentRefreshToken,
      })) as AxiosResponse<{ newAccessToken: string }>;
      const { newAccessToken } = data as unknown as { newAccessToken: string };
      if (!newAccessToken) {
        throw new Error("New access token not received from refresh endpoint.");
      }

      saveTokens(newAccessToken);
      setAuthTokenHeader(newAccessToken);

      processQueue(null, newAccessToken);

      if (originalRequest?.headers) {
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
      }
      return getApiClientInstance()(originalRequest);
    } catch (refreshError: any) {
      // console.error("Token refresh failed:", refreshError?.response?.data || refreshError.message);
      clearTokensAndLogout();
      processQueue(refreshError, null);
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  if (error?.response?.status === 403) {
    // console.error(`Forbidden (403): Access denied for ${error?.config?.url}. User may lack permissions.`);
    let normalizedError = {
      message:
        "Permission Denied: You do not have access to this resource or action.",
      status: error?.response?.status.toString(),
      details: error?.response?.data,
    };
    if (error?.response?.data) {
      try {
        const customNormalized = normalizeJsonApiIfNeed(error?.response?.data);
        if (typeof customNormalized === "object" && customNormalized !== null) {
          normalizedError = {
            ...normalizedError,
            ...customNormalized,
            status: error?.response?.status.toString(),
          };
        } else if (customNormalized) {
          normalizedError.message = String(customNormalized);
        }
      } catch (normError) {
        console.warn("Could not normalize 403 error response data:", normError);
      }
    }
    return Promise.reject(normalizedError);
  }

  let finalRejectedError: any = error; // Default to original AxiosError
  if (error?.response?.data) {
    try {
      const normalized = normalizeJsonApiIfNeed(error?.response?.data);
      if (typeof normalized === "object" && normalized !== null) {
        normalized.status = error?.response?.status.toString();
        finalRejectedError = normalized;
      } else {
        // If normalization results in non-object, wrap it
        finalRejectedError = {
          message: String(normalized),
          status: error?.response?.status.toString(),
          details: error?.response?.data,
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (normError) {
      // console.warn(`Could not normalize ${error?.response?.status} error response data:`, normError);
      finalRejectedError = {
        message:
          error?.message ||
          `Request failed with status ${error?.response?.status}`,
        status: error?.response?.status.toString(),
        details: error?.response?.data,
      };
    }
  } else {
    finalRejectedError = {
      message:
        error?.message ||
        `Request failed with status ${error?.response?.status}`,
      status: error?.response?.status.toString(),
    };
  }

  return Promise.reject(finalRejectedError);
};

const TIMEOUT = 60000; // ms

export enum AcceptType {
  json = "application/json",
  formData = "multipart/form-data",
  urlEncode = "application/x-www-form-urlencoded",
}

const defaultHeaders = {
  Accept: AcceptType.json,
  "Content-Type": AcceptType.json,
};

const formHeaders = {
  "Content-Type": AcceptType.formData,
};

const urlEncodeHeaders = {
  "Content-Type": AcceptType.urlEncode,
};

let apiInstance: AxiosInstance | null = null;

const initializeApiClient = (baseURL: string): void => {
  if (apiInstance) {
    console.warn(
      "apiClient has already been initialized. Ignoring subsequent call."
    );
    return;
  }

  if (!baseURL) {
    throw new Error("Cannot initialize apiClient without a valid baseURL.");
  }

  apiInstance = axios.create({
    baseURL: baseURL, // Use the provided baseURL
    timeout: TIMEOUT,
    headers: { ...defaultHeaders },
    validateStatus(status) {
      return status >= 200 && status < 300;
    },
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
    transitional: {
      clarifyTimeoutError: true,
    },
  });

  // apiInstance.interceptors.request.use(requestInterceptor, errorInterceptor);
  apiInstance.interceptors.request.use(requestInterceptor, (error) => {
    // Handle request configuration errors (rare)
    // console.error("Request Setup Error:", error);
    return Promise.reject(error);
  });
  apiInstance.interceptors.response.use(responseInterceptor, errorInterceptor);

  const initialToken = getAuthToken();
  if (initialToken) {
    setAuthTokenHeader(initialToken);
  }

  // console.log(`✅ apiClient initialized successfully with baseURL: ${baseURL}`);
};

const getApiClientInstance = (): AxiosInstance => {
  if (!apiInstance) {
    throw new Error(
      "apiClient has not been initialized. Call initializeApiClient(baseURL) before making API requests."
    );
  }
  return apiInstance;
};

const setAuthTokenHeader = (
  token: string | null,
  isUsingBasic: boolean = false
): void => {
  const instance = getApiClientInstance(); // Ensures instance exists
  if (token && token.length > 0) {
    const authType = isUsingBasic ? "Basic" : "Bearer";
    instance.defaults.headers.common["Authorization"] = `${authType} ${token}`;
  } else {
    delete instance.defaults.headers.common["Authorization"];
  }
};

const setAuthToken = (
  accessToken: string,
  refreshToken?: string,
  isUsingBasic: boolean = false
): void => {
  if (!accessToken) {
    clearAuthToken();
    return;
  }
  localStorageService.setItem(ACCESS_TOKEN, accessToken);
  if (refreshToken) {
    localStorageService.setItem(REFRESH_TOKEN, refreshToken);
  }
  setAuthTokenHeader(accessToken, isUsingBasic);
};

const clearAuthToken = (): void => {
  clearTokensAndLogout();
};

const get = <T = any>(
  url: string,
  params?: any,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  return instance.get<T>(url, { params, ...options });
};

const post = <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  console.log("instance", instance, url, data, options);
  return instance.post<T>(url, data, { ...options });
};

const postForm = <T = any>(
  url: string,
  data?: FormData,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  return instance.post<T>(url, data, {
    headers: { ...formHeaders },
    ...options,
  });
};

const postUrlEncoded = <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  const encodedData =
    typeof data === "string" ? data : new URLSearchParams(data).toString();
  return instance.post<T>(url, encodedData, {
    headers: { ...urlEncodeHeaders },
    ...options,
  });
};

const put = <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  return instance.put<T>(url, data, { ...options });
};

const putForm = <T = any>(
  url: string,
  data?: FormData,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  return instance.put<T>(url, data, {
    headers: { ...formHeaders },
    ...options,
  });
};

const patch = <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  return instance.patch<T>(url, data, { ...options });
};

const del = <T = any>(
  url: string,
  data?: any,
  options?: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const instance = getApiClientInstance();
  return instance.delete<T>(url, { data, ...options });
};

// --- Export the Initializer and the API Client object ---
export { initializeApiClient };

export const apiClient = {
  get,
  post,
  postForm,
  postUrlEncoded,
  put,
  putForm,
  patch,
  delete: del,
  setAuthToken,
  clearAuthToken,
  getInstance: getApiClientInstance,
};
